import {MapService, Markers} from '@/services/MapService';
import {Graphics} from '@/services/GraphicService';
import {getStore} from '@/services/store';
import {createLabelGraphic} from './LabelGraphic';
import {createFeatureContent} from '@/lib/classes/InfoWindowContent';
import {NEW_LABEL_ID} from '@/lib/symbols';

/**
 * @typedef {import('@/services/GraphicService/LabelGraphic/LabelGraphic').LabelGraphic} LabelGraphic
 */

const createNewLabel = (callback) => {
  /**
   * When the user clicks the map, create a new label at that position.
   * @param {google.maps.MapMouseEvent} event The click event.
   */
  const handleClick = async (event) => {
    exitCreateMode();
    if (!event.latLng) return;
    const worldWidth = await MapService.getWorldWidth();
    const label = createLabelGraphic({center: event.latLng, worldWidth});
    // A blank label should not be saved, so the create function must handle opening an infowindow and manage editing
    const openIWSub = label.subscribe('open_info_window', ({payload: partialContent}) => {
      openIWSub.unsubscribe();
      const content = createFeatureContent({
        type: 'feature',
        id: NEW_LABEL_ID,
        metadata: {},
        displayFields: [],
        featureType: partialContent.featureType,
        parent: partialContent.parent,
        // Editing state may be handled by the InfoWindow. The InfoWindow only needs to be able to EXIT editing
        // mode on a new label, ignore setEditing(true)
        setEditing: (newState) => {
          if (newState) return;
          label.setEditing(false);
        },
      }, partialContent.toolbar);
      getStore().commit('infowindow/updateInfoWindowContents', {content});
    });
    const stopEditSub = label.subscribe('stopped_editing', async (event) => {
      stopEditSub.unsubscribe();
      // do not save a blank label. The payload for stopped_editing will be null if the user exits editing mode without
      // entering any text.
      if (event.payload) {
        // This must be awaited to prevent a flicker when the temporary label is removed.
        await callback({
          ...event.payload,
        });
      }
      label.setMap(null);
    });
    label.setMap(MapService.getMap());
    label.setEditing(true);
  };

  /**
   * Remove event listeners and return the map to its usual state.
   */
  const exitCreateMode = () => {
    MapService.setCursor(null);
    Markers.setAllClickable(true);
    Graphics.setAllClickable(true);
    MapService.removeMapListener('createNewLabel_click');
    window.removeEventListener('keydown', escapeListener);
  };
  // Register the initial listeners:
  // On map click, create the new label.
  // On ESC key press, exit create mode.
  MapService.addMapListener('createNewLabel_click', 'click', handleClick);
  const escapeListener = (event) => {
    if (event.key === 'Escape') {
      exitCreateMode();
    }
  };
  window.addEventListener('keydown', escapeListener, {once: true});

  // Set map settings.
  MapService.setCursor('text');
  Markers.setAllClickable(false);
  Graphics.setAllClickable(false);

  return exitCreateMode;
};

export {createNewLabel};
